import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {PasskeyService, PasskeyLocalCeremonyError} from '../../_services';
import {describeThisDevice, isPasskeySupported} from '../models/passkey.util';
import {copyCodesToClipboard, downloadCodesAsFile} from '../models/recovery-codes.util';
import {completePostLogin} from '../models/post-login';
import {environment} from '../../../environments/environment';

/**
 * JS-1396 [A] — mandatory enrolment, reached from /login's
 * 2fa_enrolment_required response.
 *
 * There is no skip control and no way past this screen that yields a
 * session: spec D9 is a hard block with no grace period. "Back to login" is
 * an abandon, not a bypass — the enrolment_token stays unspent and the user
 * simply has no session.
 */
@Component({
  moduleId: module.id,
  selector: 'app-passkey-enrol',
  templateUrl: `../views/passkey-enrol.component.html`,
  styleUrls: ['../styles/auth-style.scss'],
  providers: [MessageService]
})
export class PasskeyEnrolComponent implements OnInit {
  public logoUrl = environment.logoUrl;
  public enrolmentToken: string = null;
  // JS-1396: true when reached from passkey-device-code (2FA already on, a
  // passkey is being added on another device) rather than from /login's
  // mandatory first enrolment. Copy only: the api returns no recovery codes
  // on this path because 2FA was already enabled, so enrol() goes straight
  // to finishLogin().
  public addDevice = false;
  public enrolling = false;
  public supported = isPasskeySupported();

  // JS-1396 whole-branch review finding 5: isPasskeySupported() only checks
  // API presence, not whether the device actually has an authenticator, so
  // "this browser or device cannot set up a passkey" (spec 9b's promised
  // legible failure for that lockout) never showed - a createCredential()
  // failure here just repeated the generic, transient toast. Persistent
  // on-screen copy survives past the toast, without touching
  // isPasskeySupported() itself or the passkey-verify screen.
  public createCredentialFailed = false;

  public showCodesDialog = false;
  public codes: string[] = [];
  public codesAcknowledged = false;

  // Held back until the codes are acknowledged. Routing straight into the
  // app on success would destroy this component and take the one and only
  // showing of the recovery codes with it.
  private pendingLogin: any = null;

  constructor(
    private router: Router,
    private passkeyService: PasskeyService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation && navigation.extras && navigation.extras.state;
    if (state) {
      this.enrolmentToken = (state as any).enrolmentToken;
      this.addDevice = !!(state as any).addDevice;
    }
  }

  ngOnInit() {
    // Reached directly, by refresh or a pasted URL, there is no token and no
    // way to get one here. Send them back rather than showing a dead screen.
    if (!this.enrolmentToken) {
      this.router.navigate(['/login']);
    }
  }

  async enrol() {
    if (!this.supported) {
      return;
    }
    this.enrolling = true;
    this.createCredentialFailed = false;

    // enrol() runs registerOptions -> createCredential -> registerVerify.
    // Only createCredential() is purely local: a cancelled system sheet, a
    // timeout or an abort sends no request, so the token is still good and
    // we must stay put and let them retry - dismissing the passkey prompt
    // must not cost them their login attempt. registerOptions/registerVerify
    // failures mean an HTTP call actually went out and the token is likely
    // spent, so those still bounce back to the password step for a fresh
    // one. PasskeyLocalCeremonyError is how the collapsed method still lets
    // us tell the two apart.
    let response: any;
    try {
      response = await this.passkeyService.enrol(describeThisDevice(navigator.userAgent), this.enrolmentToken);
    } catch (error) {
      if (error instanceof PasskeyLocalCeremonyError) {
        this.enrolling = false;
        // Persistent copy alongside the toast: a user with no authenticator
        // at all can retry this forever and always land back here, so the
        // administrator hint must survive past the toast's few seconds
        // (JS-1396 whole-branch review finding 5).
        this.createCredentialFailed = true;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('Error!'),
          detail: this.translate.instant('could not add passkey')
        });
        return;
      }
      this.failAndReturnToLogin();
      return;
    }

    // register-verify on this path returns the full login payload as well as
    // the codes (api Task 2), so the session is ready the moment the codes
    // are acknowledged.
    this.pendingLogin = response;

    if (response.recovery_codes) {
      this.codes = response.recovery_codes;
      this.codesAcknowledged = false;
      this.showCodesDialog = true;
      this.enrolling = false;
      return;
    }

    this.finishLogin();
  }

  /**
   * A request went out and came back an error, so the enrolment_token is
   * spent or dead. Toast, then back to the password step for a fresh one -
   * same shape as passkey-verify's equivalent path, delay included so the
   * toast can be read.
   */
  private failAndReturnToLogin() {
    this.enrolling = false;
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('Error!'),
      detail: this.translate.instant('could not add passkey')
    });
    setTimeout(() => this.router.navigate(['/login']), 2500);
  }

  confirmCodesSaved() {
    this.showCodesDialog = false;
    this.finishLogin();
  }

  copyCodes() {
    copyCodesToClipboard(this.codes);
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('Success!'),
      detail: this.translate.instant('recovery codes copied')
    });
  }

  downloadCodes() {
    downloadCodesAsFile(this.codes);
  }

  backToLogin() {
    this.router.navigate(['/login']);
  }

  private finishLogin() {
    completePostLogin(this.pendingLogin, {
      router: this.router,
      translate: this.translate,
      tempPasswordDestination: 'login'
    });
  }
}
